/**
 * The credential stripper (MYP-22, landed with MYP-105).
 *
 * `alipay_page2` returns the customer's own record — 99 fields — and the record carries secrets:
 * the account password, the payment PIN, the Taobao password, the forgotten-password code, and the
 * paths of the uploaded identity-document images. MYP-13 flags "credential leakage in
 * alipay_page2" as an open concern.
 *
 * The account adapter is an allow-list, so it never *reads* those fields. That is not enough on its
 * own: a caller that keeps the raw response — in React state, in a store, in a log line — has the
 * secrets in client memory whatever the adapter later ignores. So they are removed here, in the
 * transport layer, straight after the body is parsed and before any caller or adapter sees it. No
 * code above `LegacyApiClient` can hold one, because none is ever handed one.
 *
 * What this cannot do: the browser's own network panel still shows the response as the vendor sent
 * it. The client can decide what it keeps, not what the server sends. Removing them at source is a
 * vendor question.
 */

/**
 * The fields named for removal in MYP-105 / MYP-22, compared case-insensitively and exactly.
 *
 * Exact names rather than a pattern for these because they are known: a pattern would be a guess
 * at the vendor's spelling, while a list can be checked against a real record.
 */
export const CREDENTIAL_KEYS: ReadonlySet<string> = new Set([
  'password',
  'paypwd',
  'tbpw',
  'forgetpwdvc',
  'idimgsrc',
  'listimgsrc',
]);

/**
 * A backstop for the ones nobody has noticed yet.
 *
 * The record has 99 fields and its SMTP block alone (`smtphost`, `smtpuser`, `smtpemail`) implies
 * a password field beside it whose name has not been confirmed. Any key that says it is a password
 * — `pwd` or `passw` anywhere in the name — is treated as one. Over-stripping costs a field nothing
 * reads; under-stripping leaks a secret.
 */
const CREDENTIAL_KEY_PATTERN = /pwd|passw/i;

export function isCredentialKey(key: string): boolean {
  return CREDENTIAL_KEYS.has(key.toLowerCase()) || CREDENTIAL_KEY_PATTERN.test(key);
}

/**
 * A deep copy of `value` with every credential field removed, at any depth and inside arrays.
 *
 * A copy, never a mutation: the caller may still hold the parsed body. Anything that is not a plain
 * object or an array (strings, numbers, null) passes through unchanged.
 */
export function stripCredentials(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripCredentials);

  if (typeof value === 'object' && value !== null) {
    const clean: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(value)) {
      if (isCredentialKey(key)) continue;
      clean[key] = stripCredentials(inner);
    }
    return clean;
  }

  return value;
}
